<script setup lang="ts">
import {CourseService} from "@/api/api.ts";
import {ref, reactive, computed} from "vue";
import {ElNotification} from "element-plus";
import {CourseComment, SimpleSection} from "@/types";
import CourseCommentItem from "components/course/comment/CourseCommentItem.vue";
import {Loading, Search} from "@element-plus/icons-vue"
import CommentReplyDialog from "components/course/comment/CommentReplyDialog.vue";

const replyDialog = ref<InstanceType<typeof CommentReplyDialog>>()
const loadingFirst = ref(false)
const loading = ref(false)
const noMore = ref(false)

const loadingRemote = ref(false)
const type = ref(0)
const courseId = ref()
const courseList = ref<SimpleSection[]>([])
const way = ref(1)
const content = ref('')
const user_name = ref('')
const courseCommentList = ref<CourseComment[]>([])
const currentParentItem = ref<CourseComment>()
const replyContent = ref('')
const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0
})

const disabled = computed(() => loading.value || noMore.value)

const getCourseCommentList = async () => {
  try {
    loading.value = true
    if (pagination.current === 1) {
      loadingFirst.value = true
    }
    const res = await CourseService.getCourseCommentList({
      page: pagination.current,
      pageSize: pagination.pageSize,
      type: type.value,
      id: courseId.value,
      way: way.value,
      content: content.value,
      user_name: user_name.value,
    })
    if (res.data.code === 200) {
      noMore.value = !(res.data.data?.list && res.data.data.list.length > 0);
      if (res.data.data?.page === 1) {
        courseCommentList.value = res.data.data.list
      } else {
        if (res.data.data?.list && res.data.data?.list.length > 0) {
          courseCommentList.value.push(...res.data.data.list)
        }
      }
      pagination.current = res.data.data?.page || 1
      pagination.total = res.data.data?.count || 0
    } else {
      ElNotification.error({
        title: '失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (error) {
    console.log(error)
  } finally {
    loading.value = false
    loadingFirst.value = false
  }
}

const replyCourseComment = async () => {
  if (!replyContent.value) {
    return ElNotification.warning({
      title: "注意",
      message: "回复内容不能为空",
      type: "error",
    })
  }
  const res = await CourseService.replyCourseComment({
    course_id: currentParentItem.value?.course_id || 0,
    comment_id: currentParentItem.value?.id || 0,
    content: replyContent.value,
  })
  if (res.data.code === 200) {
    ElNotification.success({
      title: "回复成功",
      message: res.data.msg,
      type: "success",
    })
    replyDialog.value?.hide()
    onSearch()
  } else {
    ElNotification.error({
      title: "回复失败",
      message: res.data.msg,
      type: "error",
    })
  }
}

const onReply = (item: CourseComment) => {
  currentParentItem.value = item
  replyDialog.value?.show()
}

const load = () => {
  // 加载更多
  if (!noMore.value) {
    pagination.current++
    getCourseCommentList()
  }
}

const onSubmit = (_replyContent: string) => {
  replyContent.value = _replyContent
  replyCourseComment()
}

const onCourseTypeChange = () => {
  courseId.value = undefined
  courseList.value = []
  newSearch()
}

const remoteMethod = (query: string) => {
  if (query) {
    getCourseList(query)
  } else {
    courseList.value = []
  }
}

const getCourseList = async (title: string) => {
  try {
    loadingRemote.value = true
    const res = await CourseService.getCourseSectionList({
      type: type.value,
      title: title,
    })
    if (res.data.code === 200) {
      courseList.value = [{id: 0, title: "全部课程"}, ...res.data.data!!]
    } else {
      ElNotification.error({
        title: '获取课程列表失败',
        message: res.data.msg,
        type: 'error'
      })
    }
  } catch (err) {
    console.warn(err)
  } finally {
    loadingRemote.value = false
  }
}

const onCourseChange = async () => {
  onSearch()
}

const newSearch = () => {
  pagination.current = 1
  noMore.value = false
  way.value = 1
  content.value = ""
  user_name.value = ""
  getCourseCommentList()
}

const onWayChange = (_way: number) => {
  way.value = _way
  onSearch()
}

const onSearch = () => {
  pagination.current = 1
  noMore.value = false
  getCourseCommentList()
}

getCourseCommentList()
</script>

<template>
  <div class="mx-3rem mt-2.25rem bg-white rounded-md shadow-md pt-1rem">
    <el-tabs v-model="type" @tab-change="onCourseTypeChange" class="px-2rem tab-font">
      <el-tab-pane label="全部评价" :name="0"></el-tab-pane>
      <el-tab-pane label="直播课评价" :name="1"></el-tab-pane>
      <el-tab-pane label="录播课评价" :name="2"></el-tab-pane>
    </el-tabs>
    <div class="relative">
      <div class="flex items-center mt-1rem gap-1rem ml-2rem">
        <el-input v-model="content" style="width: 12rem" :suffix-icon="Search" placeholder="关键字搜索"
                  @change="onSearch"></el-input>
        <el-input v-model="user_name" style="width: 12rem;" :suffix-icon="Search" placeholder="用户名搜索"
                  @change="onSearch"></el-input>
        <el-select
            v-model="courseId"
            style="width: 12rem"
            filterable remote remote-show-suffix :remote-method="remoteMethod"
            placeholder="请输入课程标题" @change="onCourseChange">
          <el-option
              v-for="item in courseList"
              :key="item.id"
              :label="item.title"
              :value="item.id">
          </el-option>
        </el-select>
        <div class="absolute flex items-center mt-.5rem right-2rem top-0 gap-.5rem">
          <div class="cursor-pointer" :class="way === 1 ? `text-#FF6600CC` : `text-#bfbfbf`" @click="onWayChange(1)">
            最新发布
          </div>
          <div class="w-0 border-l-0 border-r h-16px border-#bfbfbf border-dashed"></div>
          <div class="cursor-pointer" :class="way === 2 ? `text-#FF6600CC` : `text-#bfbfbf`" @click="onWayChange(2)">
            点赞最多
          </div>
          <div class="w-0 border-l-0 border-r h-16px border-#bfbfbf border-dashed"></div>
          <div class="cursor-pointer" :class="way === 3 ? `text-#FF6600CC` : `text-#bfbfbf`" @click="onWayChange(3)">
            评分最高
          </div>
        </div>
      </div>
      <div
          v-loading="loadingFirst"
          v-infinite-scroll="load"
          :infinite-scroll-disabled="disabled">
        <template v-for="item in courseCommentList" :key="item.id">
          <course-comment-item :comment="item" @on-reply="onReply"></course-comment-item>
        </template>
        <div class="flex justify-center" v-if="loading">
          <p class="mx-auto text-#939393">
            <el-icon>
              <Loading class="animate-spin"></Loading>
            </el-icon>
            加载中...
          </p>
        </div>
        <el-empty v-if="courseCommentList.length === 0" class="mt-2.5rem" description="没有评论~"></el-empty>
        <div class="flex justify-center" v-if="noMore && courseCommentList.length > 0">
          <p class="mx-auto text-#939393">没有更多了...</p>
        </div>
      </div>
    </div>
  </div>
  <comment-reply-dialog ref="replyDialog" @on-submit="onSubmit"/>
</template>

<style scoped>
.tab-font {
  --el-font-size-base: 18px;
}
</style>
